import { ShiftTableRow, shiftTableColumns } from './lib/types';
import type { DerivedTeamRoster } from '../../hooks/types';
import useQueryTeamRosterAudit from '../../hooks/useQueryTeamRosterAudit';
import { onApolloError } from '../../lib/graphql/helpers';
import { useNotify } from '../../hooks/useNotify';
import { useState } from 'react';
import PaginatedTable, { DEFAULTPAGESIZE } from '../PaginatedTable/index';
import { useDebounceEffect } from 'ahooks';
import { searchDebounceMillis } from './lib/util';
import { rostersToShiftTableRows } from './shiftTable';

type ExportShiftTableProps = {
  readonly selectedTeamIds: readonly string[];
  readonly selectedTeamTypes: readonly string[];
  readonly selectedCostObjectIds: readonly string[];
  readonly selectedTeamRoleIds: readonly string[];
  readonly selectedTeamRoleGroups: readonly string[];
  readonly selectedUserIds: readonly string[];
  readonly selectedUserTypes: readonly string[];
  readonly fromDate: Date;
  readonly toDate: Date;
  readonly shiftFilter:
    | 'only vacant shifts'
    | 'only non-vacant shifts'
    | 'all shifts';
  readonly queriedTeamRoster: readonly DerivedTeamRoster[];
} & {
  readonly setQueriedTeamRoster: (value: readonly DerivedTeamRoster[]) => void;
  readonly setMessage: (value: string) => void;
};

/**
 * Renders the table for exporting shift.
 */
const ExportShiftTable: React.FC<ExportShiftTableProps> = ({
  fromDate,
  toDate,
  shiftFilter,
  selectedCostObjectIds,
  selectedTeamIds,
  selectedTeamRoleGroups,
  selectedTeamRoleIds,
  selectedTeamTypes,
  selectedUserIds,
  selectedUserTypes,
  queriedTeamRoster,
  setQueriedTeamRoster,
  setMessage,
}) => {
  const { notify } = useNotify();
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULTPAGESIZE);

  // Shift table rows to be displayed
  const [shiftTableRows, setShiftTableRows] = useState<
    readonly ShiftTableRow[]
  >([]);
  //////////////////////////////
  // Search and process shift //
  //////////////////////////////
  const { loading: loadingShift } = useQueryTeamRosterAudit({
    teamIds: selectedTeamIds,
    teamTypes: selectedTeamTypes,
    costObjectIds: selectedCostObjectIds,
    teamRoleIds: selectedTeamRoleIds,
    teamRoleGroups: selectedTeamRoleGroups,
    userIds: selectedUserIds,
    userTypes: selectedUserTypes,
    from: fromDate,
    to: toDate,
    onCompleted: (teamRoster: readonly DerivedTeamRoster[], msg: string) => {
      setQueriedTeamRoster(teamRoster);
      setMessage(msg);
    },
    onError: onApolloError(notify.error),
  });

  useDebounceEffect(
    () => {
      setShiftTableRows(
        rostersToShiftTableRows(queriedTeamRoster, shiftFilter)
      );
      setCurrentPage(1);
    },
    [queriedTeamRoster, shiftFilter],
    { wait: searchDebounceMillis }
  );
  return (
    <PaginatedTable
      otherTableProps={{
        id: 'export-shift-table',
        dataSource: shiftTableRows,
        columns: shiftTableColumns(shiftFilter !== 'only vacant shifts'),
        style: { marginTop: '10px' },
        scroll: { x: true },
        loading: loadingShift,
      }}
      pagination={{
        page: {
          currentPage,
          setCurrentPage,
        },
        pageSize: {
          currentPageSize,
          setCurrentPageSize,
        },
        otherPaginationProps: { position: ['bottomRight'] },
      }}
    />
  );
};
export default ExportShiftTable;
